import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaPlay, FaPause, FaExpand, FaCompress, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';

function VideoPlayer({ src, title = 'فيديو' }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const progressRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const hideTimerRef = useRef(null);

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (!document.fullscreenElement) {
      container.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  const skip = useCallback((seconds) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.max(0, Math.min(video.duration, video.currentTime + seconds));
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate = () => setCurrentTime(video.currentTime);
    const onLoadedMetadata = () => {
      setDuration(video.duration);
      setIsLoading(false);
    };
    const onEnded = () => setIsPlaying(false);
    const onWaiting = () => setIsLoading(true);
    const onCanPlay = () => setIsLoading(false);

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('ended', onEnded);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('canplay', onCanPlay);

    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('canplay', onCanPlay);
    };
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    clearTimeout(hideTimerRef.current);
    if (isPlaying) {
      hideTimerRef.current = setTimeout(() => setShowControls(false), 3000);
    }
  }, [isPlaying]);

  useEffect(() => {
    if (!isPlaying) {
      setShowControls(true);
      clearTimeout(hideTimerRef.current);
    } else {
      hideTimerRef.current = setTimeout(() => setShowControls(false), 3000);
    }
    return () => clearTimeout(hideTimerRef.current);
  }, [isPlaying]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      ref={containerRef}
      className="relative aspect-video bg-black rounded-lg overflow-hidden shadow-md group select-none"
      onMouseMove={resetHideTimer}
      onMouseLeave={() => isPlaying && setShowControls(false)}
    >
      <video
        ref={videoRef}
        src={src}
        className="w-full h-full object-contain"
        onClick={togglePlay}
        playsInline
        crossOrigin="use-credentials"
      />

      {/* Loading spinner */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
          <div className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
        </div>
      )}

      {/* Big center play button (when paused) */}
      {!isPlaying && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center cursor-pointer" onClick={togglePlay}>
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary/90 rounded-full flex items-center justify-center shadow-xl hover:bg-primary transition-colors">
            <FaPlay className="text-white text-xl sm:text-2xl ml-1" />
          </div>
        </div>
      )}

      {/* Top title bar - YouTube style, auto-hide with controls */}
      <div
        className={`absolute top-0 left-0 right-0 z-10 transition-opacity duration-300 pointer-events-none ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, transparent 100%)' }}
      >
        <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3" dir="rtl">
          <img src="/icon.png" alt="Allemni" className="w-6 h-6 sm:w-7 sm:h-7 rounded" />
          <span className="text-white font-bold text-xs sm:text-sm truncate">{title}</span>
        </div>
      </div>

      {/* Branding badge - ALWAYS visible, bottom left, scales up in fullscreen */}
      <div className={`absolute rounded-md pointer-events-none z-10 ${
        isFullscreen
          ? 'bottom-[38px] left-[6px]'
          : 'bottom-[20px] sm:bottom-[23px] left-[2px] sm:left-[4px]'
      }`} style={{
        background: isFullscreen
          ? 'linear-gradient(135deg, rgba(0,42,108,0.98), rgba(0,72,194,0.98))'
          : 'linear-gradient(135deg, rgba(0,42,108,0.93), rgba(0,72,194,0.91))',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      }}>
        <div className={`flex items-center ${
          isFullscreen
            ? 'gap-2.5 pl-7 pr-10 py-3'
            : 'gap-1 sm:gap-2 px-2.5 py-1.5 sm:px-4 sm:py-2'
        }`}>
          <img src="/icon.png" alt="Allemni" className={`rounded ${
            isFullscreen ? 'w-8 h-8' : 'w-5 h-5 sm:w-6 sm:h-6'
          }`} />
          <span className={`text-white font-bold ${
            isFullscreen ? 'text-[14px]' : 'text-[10px] sm:text-xs'
          }`}>Allemni</span>
          <span className={`text-white/50 ${
            isFullscreen ? 'text-xs' : 'text-[8px] sm:text-[10px]'
          }`}>علمني بالدارجة</span>
        </div>
      </div>

      {/* Player controls - auto-hide */}
      <div
        className={`absolute bottom-0 left-0 right-0 transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, transparent 100%)' }}
      >
        {/* Progress bar - padded for easier clicking */}
        <div
          ref={progressRef}
          className="cursor-pointer mx-2 sm:mx-3 py-3 relative"
          dir="ltr"
          onMouseDown={(e) => {
            e.preventDefault();
            const video = videoRef.current;
            const track = progressRef.current?.querySelector('[data-track]');
            if (!video || !track) return;
            const seek = (evt) => {
              const rect = track.getBoundingClientRect();
              const pos = Math.max(0, Math.min(1, (evt.clientX - rect.left) / rect.width));
              video.currentTime = pos * video.duration;
            };
            seek(e);
            const onMove = (evt) => { evt.preventDefault(); seek(evt); };
            const onUp = () => {
              document.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
          }}
        >
          <div data-track className="h-1 sm:h-1.5 bg-white/20 rounded-full relative overflow-visible pointer-events-none">
            <div
              className="h-full bg-primary rounded-full"
              style={{ width: `${progress}%` }}
            />
            <div
              className="absolute top-1/2 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-white rounded-full shadow-md border-2 border-primary"
              style={{ left: `${progress}%`, transform: `translate(-50%, -50%)` }}
            />
          </div>
        </div>

        <div className="flex items-center justify-end px-3 sm:px-4 py-2 sm:py-2.5" dir="ltr">
          <div className="flex items-center gap-3 sm:gap-4">
            <button onClick={() => skip(-10)} className="text-white/70 hover:text-white transition-colors text-xs sm:text-sm font-bold">
              -10
            </button>

            <button onClick={togglePlay} className="text-white hover:text-primary-light transition-colors">
              {isPlaying ? <FaPause className="text-base sm:text-lg" /> : <FaPlay className="text-base sm:text-lg" />}
            </button>

            <button onClick={() => skip(10)} className="text-white/70 hover:text-white transition-colors text-xs sm:text-sm font-bold">
              +10
            </button>

            <span className="text-white/80 text-[10px] sm:text-sm font-mono tabular-nums">
              {formatTime(currentTime)}/{formatTime(duration)}
            </span>

            <button onClick={toggleMute} className="text-white/70 hover:text-white transition-colors">
              {isMuted ? <FaVolumeMute className="text-sm sm:text-base" /> : <FaVolumeUp className="text-sm sm:text-base" />}
            </button>

            <button onClick={toggleFullscreen} className="text-white/70 hover:text-white transition-colors">
              {isFullscreen ? <FaCompress className="text-sm sm:text-base" /> : <FaExpand className="text-sm sm:text-base" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VideoPlayer;
